import React, {useEffect, useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {Link} from 'react-router-dom';
import AccountHeroBit from '../../components/account/AccountHeroBit';
import OrderCard from '../../components/account/OrderCard';
import LuxuryButton from '../../components/ui/LuxuryButton';
import LuxuryInput from '../../components/ui/LuxuryInput';
import {
    FaBox,
    FaTruck,
    FaCheckCircle,
    FaClock,
    FaSearch,
    FaFilter,
    FaDownload,
    FaRedo,
    FaShoppingBag,
    FaArrowRight,
    FaReceipt,
    FaStar,
    FaTimesCircle,
    FaExclamationTriangle,
    FaCreditCard
} from 'react-icons/fa';
import {fadeInUp, pageTransition} from "../../components/animations/animations";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { OrderService } from '../../api/order.api';

const Orders = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [timeRange, setTimeRange] = useState('all-time');
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const ordersData = await OrderService.getMyOrders();
            console.log('Orders data received:', ordersData);
            setOrders(Array.isArray(ordersData) ? ordersData : []);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError(err.message || 'Failed to load orders. Please try again.');
            setOrders([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Get order status display (prioritizes orderStatus, falls back to paymentStatus)
    const getOrderStatus = (order) => {
        if (order.orderStatus) return order.orderStatus;
        if (order.paymentStatus === 'completed') return 'confirmed';
        if (order.paymentStatus === 'processing') return 'processing';
        return 'pending';
    };

    const filteredOrders = orders.filter(order => {
        if (!order) return false;

        // Get the actual status for filtering
        const orderStatus = getOrderStatus(order);

        // Status filter
        if (filter !== 'all' && orderStatus !== filter) return false;

        // Search filter - search by order number or product names
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesOrderNumber = order.orderNumber?.toLowerCase().includes(searchLower);
            const matchesProductNames = order.orderItems?.some(item =>
                item.name?.toLowerCase().includes(searchLower)
            );
            if (!matchesOrderNumber && !matchesProductNames) return false;
        }

        // Time range filter
        if (!order.createdAt) return true;

        const orderDate = new Date(order.createdAt);
        const now = new Date();
        const daysDiff = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));

        if (timeRange === 'last-30' && daysDiff > 30) return false;
        if (timeRange === 'last-90' && daysDiff > 90) return false;
        if (timeRange === 'last-year' && daysDiff > 365) return false;

        return true;
    });

    // Calculate statistics based on orderStatus
    const calculateStats = () => {
        let total = 0;
        let delivered = 0;
        let shipped = 0;
        let processing = 0;
        let cancelled = 0;
        let totalSpent = 0;

        orders.forEach(order => {
            const status = getOrderStatus(order);
            total++;
            totalSpent += order.total || 0;

            switch (status) {
                case 'delivered':
                    delivered++;
                    break;
                case 'shipped':
                    shipped++;
                    break;
                case 'processing':
                case 'confirmed':
                    processing++;
                    break;
                case 'cancelled':
                    cancelled++;
                    break;
            }
        });

        return { total, delivered, shipped, processing, cancelled, totalSpent };
    };

    const stats = calculateStats();

    // Get all unique statuses for filter dropdown
    const getAllStatuses = () => {
        const statuses = new Set();
        orders.forEach(order => {
            const status = getOrderStatus(order);
            if (status) statuses.add(status);
        });
        return Array.from(statuses).sort();
    };

    const statuses = getAllStatuses();

    const handleReorder = async (orderId) => {
        setIsLoading(true);
        try {
            console.log('Reordering:', orderId);
            await new Promise(resolve => setTimeout(resolve, 1000));
            alert('Reorder initiated! Items have been added to your cart.');
        } catch (err) {
            console.error('Reorder failed:', err);
            alert('Failed to reorder. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadInvoice = async (orderId) => {
        try {
            await OrderService.downloadInvoice(orderId);
        } catch (err) {
            console.error('Failed to download invoice:', err);
            alert('Failed to download invoice. Please try again.');
        }
    };

    const handleReturn = (orderId) => {
        console.log('Initiating return for:', orderId);
        alert('Return process would start here. This feature is not yet implemented.');
    };

    const handleTrackOrder = async (order) => {
        try {
            const trackingInfo = await OrderService.trackOrder(order._id);
            console.log('Tracking info:', trackingInfo);
            alert(`Tracking: ${trackingInfo.trackingNumber || 'No tracking available'}`);
        } catch (err) {
            console.error('Tracking failed:', err);
            alert('Unable to track order at this time.');
        }
    };

    const handleCancelOrder = async (orderId, reason) => {
        if (!reason) {
            reason = prompt('Please provide a reason for cancellation:');
            if (!reason) return;
        }

        if (!window.confirm('Are you sure you want to cancel this order?')) return;

        try {
            await OrderService.cancelOrder(orderId, reason);
            await fetchOrders();
            alert('Order cancelled successfully.');
        } catch (err) {
            console.error('Cancel failed:', err);
            alert('Failed to cancel order: ' + err.message);
        }
    };

    const toggleOrderDetails = (orderId) => {
        setExpandedOrder(expandedOrder === orderId ? null : orderId);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'delivered':
            case 'confirmed':
                return FaCheckCircle;
            case 'shipped':
                return FaTruck;
            case 'processing':
                return FaClock;
            case 'cancelled':
                return FaTimesCircle;
            default:
                return FaBox;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'delivered':
            case 'confirmed':
                return 'text-sage';
            case 'shipped':
                return 'text-champagne';
            case 'processing':
                return 'text-amber-500';
            case 'cancelled':
                return 'text-red-500';
            default:
                return 'text-charcoal/50';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'confirmed':
                return 'Confirmed';
            case 'processing':
                return 'Processing';
            case 'pending':
                return 'Pending';
            case 'shipped':
                return 'Shipped';
            case 'delivered':
                return 'Delivered';
            case 'cancelled':
                return 'Cancelled';
            default:
                return status?.charAt(0)?.toUpperCase() + status?.slice(1) || 'Unknown';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 overflow-x-hidden">
            {/* Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar/>
            </div>

            <motion.div
                variants={pageTransition}
                initial="initial"
                animate="animate"
                exit="exit"
                className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 py-12 pt-28 pb-16"
            >
                <div className="container-luxury max-w-7xl mx-auto px-4">
                    {/* Hero Section */}
                    <AccountHeroBit
                        title="Order History"
                        subtitle="Track your beauty journey, one order at a time"
                        background="champagne"
                    >
                        <motion.div
                            variants={fadeInUp}
                            className="flex flex-wrap items-center gap-4 mt-6"
                        >
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <FaBox className="w-4 h-4 text-champagne"/>
                                <span className="text-charcoal/70">{stats.total} orders</span>
                            </div>

                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <FaCheckCircle className="w-4 h-4 text-sage"/>
                                <span className="text-charcoal/70">{stats.delivered} delivered</span>
                            </div>

                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <FaReceipt className="w-4 h-4 text-champagne"/>
                                <span className="text-charcoal/70">${stats.totalSpent.toFixed(2)} total</span>
                            </div>
                        </motion.div>
                    </AccountHeroBit>

                    {/* Loading State */}
                    {isLoading && !error && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-16"
                        >
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-champagne border-t-transparent"></div>
                            <p className="mt-4 text-charcoal/60">Loading your orders...</p>
                        </motion.div>
                    )}

                    {/* Error State */}
                    {error && !isLoading && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-red-50 border border-red-200 rounded-2xl p-8 mb-8 text-center"
                        >
                            <FaExclamationTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-red-800 mb-2">Failed to Load Orders</h3>
                            <p className="text-red-600 mb-4">{error}</p>
                            <LuxuryButton
                                variant="primary"
                                size="medium"
                                onClick={fetchOrders}
                            >
                                Try Again
                            </LuxuryButton>
                        </motion.div>
                    )}

                    {/* Main Content - Only show if not loading and no error */}
                    {!isLoading && !error && (
                        <>
                            {/* Stats Overview */}
                            <motion.div
                                variants={fadeInUp}
                                className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8"
                            >
                                {[
                                    {
                                        label: 'Total Orders',
                                        value: stats.total,
                                        icon: FaBox,
                                        color: 'from-champagne/10 to-champagne/5'
                                    },
                                    {
                                        label: 'Confirmed',
                                        value: stats.delivered,
                                        icon: FaCheckCircle,
                                        color: 'from-sage/10 to-sage/5'
                                    },
                                    {
                                        label: 'Processing',
                                        value: stats.processing,
                                        icon: FaClock,
                                        color: 'from-nude/20 to-nude/10'
                                    },
                                    {
                                        label: 'Cancelled',
                                        value: stats.cancelled,
                                        icon: FaTimesCircle,
                                        color: 'from-red-100 to-red-50'
                                    },
                                    {
                                        label: 'Total Spent',
                                        value: `$${stats.totalSpent.toFixed(2)}`,
                                        icon: FaCreditCard,
                                        color: 'from-champagne/5 to-transparent'
                                    }
                                ].map((stat, index) => (
                                    <motion.div
                                        key={stat.label}
                                        initial={{opacity: 0, y: 20}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{delay: index * 0.1}}
                                        whileHover={{y: -4}}
                                        className={`p-6 rounded-2xl bg-gradient-to-r ${stat.color} border border-white/50`}
                                    >
                                        <div className="flex items-center gap-3 mb-3">
                                            <div
                                                className="w-10 h-10 rounded-full bg-white/50 flex items-center justify-center">
                                                <stat.icon className="w-4 h-4 text-champagne"/>
                                            </div>
                                            <div className="text-2xl font-light text-charcoal">{stat.value}</div>
                                        </div>
                                        <p className="text-sm text-charcoal/50">{stat.label}</p>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Controls */}
                            <motion.div
                                variants={fadeInUp}
                                className="flex flex-col lg:flex-row gap-6 justify-between items-start lg:items-center mb-8 p-6 bg-white rounded-2xl shadow-subtle"
                            >
                                <div className="flex-1 w-full lg:w-auto">
                                    <LuxuryInput
                                        type="text"
                                        placeholder="Search orders by number or product..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        icon={FaSearch}
                                        size="medium"
                                        className="w-full lg:w-96"
                                    />
                                </div>

                                <div className="flex flex-wrap gap-4">
                                    {/* Filter by Status */}
                                    <div className="relative">
                                        <select
                                            value={filter}
                                            onChange={(e) => setFilter(e.target.value)}
                                            className="appearance-none bg-transparent border border-nude rounded-xl px-4 py-2 pr-10 text-sm text-charcoal focus:outline-none focus:border-champagne cursor-pointer"
                                        >
                                            <option value="all">All Status</option>
                                            {statuses.map(status => (
                                                <option key={status} value={status}>
                                                    {getStatusText(status)}
                                                </option>
                                            ))}
                                        </select>
                                        <div
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <FaFilter className="w-3 h-3 text-charcoal/40"/>
                                        </div>
                                    </div>

                                    {/* Time Range */}
                                    <div className="relative">
                                        <select
                                            value={timeRange}
                                            onChange={(e) => setTimeRange(e.target.value)}
                                            className="appearance-none bg-transparent border border-nude rounded-xl px-4 py-2 pr-10 text-sm text-charcoal focus:outline-none focus:border-champagne cursor-pointer"
                                        >
                                            <option value="all-time">All Time</option>
                                            <option value="last-30">Last 30 Days</option>
                                            <option value="last-90">Last 90 Days</option>
                                            <option value="last-year">Last Year</option>
                                        </select>
                                        <div
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <svg className="w-3 h-3 text-charcoal/40" fill="none" stroke="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                            </svg>
                                        </div>
                                    </div>

                                    <LuxuryButton
                                        variant="outline"
                                        size="small"
                                        onClick={() => {
                                            setFilter('all');
                                            setSearchTerm('');
                                            setTimeRange('all-time');
                                        }}
                                    >
                                        Clear Filters
                                    </LuxuryButton>
                                </div>
                            </motion.div>

                            {/* Orders List */}
                            <AnimatePresence>
                                {filteredOrders.length > 0 ? (
                                    <motion.div
                                        variants={fadeInUp}
                                        className="space-y-6"
                                    >
                                        {filteredOrders.map((order, index) => {
                                            const orderStatus = getOrderStatus(order);
                                            const StatusIcon = getStatusIcon(orderStatus);

                                            return (
                                                <motion.div
                                                    key={order._id}
                                                    initial={{opacity: 0, y: 20}}
                                                    animate={{opacity: 1, y: 0}}
                                                    transition={{delay: index * 0.05}}
                                                    layout
                                                >
                                                    {/* Order Card */}
                                                    <div className="bg-white rounded-2xl shadow-subtle overflow-hidden">
                                                        <div className="p-6">
                                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-3 mb-2">
                                                                        <h3 className="text-lg font-medium text-charcoal">
                                                                            {order.orderNumber}
                                                                        </h3>
                                                                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${getStatusColor(orderStatus)}`}>
                                                                            <StatusIcon className="w-3 h-3" />
                                                                            {getStatusText(orderStatus)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex flex-wrap items-center gap-4 text-sm">
                                                                        <span className="text-charcoal/60">
                                                                            {formatDate(order.createdAt)}
                                                                        </span>
                                                                        <span className="text-charcoal font-medium">
                                                                            ${(order.total || 0).toFixed(2)}
                                                                        </span>
                                                                        <span className="text-charcoal/60">
                                                                            {order.orderItems?.length || 0} item{order.orderItems?.length !== 1 ? 's' : ''}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center gap-3">
                                                                    <LuxuryButton
                                                                        variant="outline"
                                                                        size="small"
                                                                        onClick={() => toggleOrderDetails(order._id)}
                                                                    >
                                                                        {expandedOrder === order._id ? 'Hide Details' : 'View Details'}
                                                                    </LuxuryButton>
                                                                    <LuxuryButton
                                                                        variant="secondary"
                                                                        size="small"
                                                                        onClick={() => handleReorder(order._id)}
                                                                        disabled={isLoading}
                                                                        icon={FaRedo}
                                                                    >
                                                                        Reorder
                                                                    </LuxuryButton>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Expanded Details */}
                                                        <AnimatePresence>
                                                            {expandedOrder === order._id && (
                                                                <motion.div
                                                                    initial={{opacity: 0, height: 0}}
                                                                    animate={{opacity: 1, height: 'auto'}}
                                                                    exit={{opacity: 0, height: 0}}
                                                                    className="border-t border-nude/30 p-6 bg-nude/5 overflow-hidden"
                                                                >
                                                                    <div className="grid md:grid-cols-2 gap-8">
                                                                        {/* Order Details */}
                                                                        <div>
                                                                            <h4 className="font-medium text-charcoal mb-4">Order Details</h4>
                                                                            <div className="space-y-3">
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-sm text-charcoal/50">Order Number</span>
                                                                                    <span className="text-charcoal">{order.orderNumber}</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-sm text-charcoal/50">Order Date</span>
                                                                                    <span className="text-charcoal">{formatDate(order.createdAt)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-sm text-charcoal/50">Status</span>
                                                                                    <span className={`font-medium ${getStatusColor(orderStatus)}`}>
                                                                                        {getStatusText(orderStatus)}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-sm text-charcoal/50">Payment Status</span>
                                                                                    <span className="font-medium">
                                                                                        {order.paymentStatus?.charAt(0).toUpperCase() + order.paymentStatus?.slice(1)}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-sm text-charcoal/50">Subtotal</span>
                                                                                    <span className="text-charcoal">${(order.subtotal || 0).toFixed(2)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-sm text-charcoal/50">Shipping</span>
                                                                                    <span className="text-charcoal">${(order.shippingCost || 0).toFixed(2)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-sm text-charcoal/50">Tax (GST)</span>
                                                                                    <span className="text-charcoal">${(order.gst || 0).toFixed(2)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between border-t border-nude/30 pt-3">
                                                                                    <span className="text-sm text-charcoal/50 font-medium">Total</span>
                                                                                    <span className="text-charcoal font-bold">${(order.total || 0).toFixed(2)}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Items List */}
                                                                        <div>
                                                                            <h4 className="font-medium text-charcoal mb-4">Items</h4>
                                                                            <div className="space-y-3">
                                                                                {order.orderItems?.map((item, itemIndex) => (
                                                                                    <div key={item._id || itemIndex}
                                                                                         className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/50 transition-colors">
                                                                                        <img
                                                                                            src={item.image || 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6'}
                                                                                            alt={item.name}
                                                                                            className="w-12 h-12 rounded-lg object-cover"
                                                                                        />
                                                                                        <div className="flex-1">
                                                                                            <p className="text-sm font-medium text-charcoal">{item.name}</p>
                                                                                            <p className="text-xs text-charcoal/50">
                                                                                                {item.variant?.name} • ${(item.price || 0).toFixed(2)} × {item.quantity || 1}
                                                                                            </p>
                                                                                        </div>
                                                                                        <div className="text-sm font-medium text-charcoal">
                                                                                            ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Shipping Address */}
                                                                    {order.shippingAddress && (
                                                                        <div className="mt-6 pt-6 border-t border-nude/30">
                                                                            <h4 className="font-medium text-charcoal mb-4">Shipping Address</h4>
                                                                            <div className="bg-white/50 rounded-xl p-4">
                                                                                <p className="text-sm text-charcoal">
                                                                                    <strong>{order.shippingAddress.name}</strong>
                                                                                </p>
                                                                                <p className="text-sm text-charcoal/70">{order.shippingAddress.address}</p>
                                                                                <p className="text-sm text-charcoal/70">
                                                                                    {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
                                                                                </p>
                                                                                <p className="text-sm text-charcoal/70">{order.shippingAddress.country}</p>
                                                                                <p className="text-sm text-charcoal/70 mt-2">
                                                                                    📧 {order.shippingAddress.email}
                                                                                </p>
                                                                                <p className="text-sm text-charcoal/70">
                                                                                    📱 {order.shippingAddress.phone}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Actions */}
                                                                    <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-nude/30">
                                                                        <LuxuryButton
                                                                            variant="outline"
                                                                            size="small"
                                                                            onClick={() => handleDownloadInvoice(order._id)}
                                                                            icon={FaDownload}
                                                                        >
                                                                            Download Invoice
                                                                        </LuxuryButton>

                                                                        {(orderStatus === 'delivered' || orderStatus === 'confirmed') && (
                                                                            <LuxuryButton
                                                                                variant="outline"
                                                                                size="small"
                                                                                onClick={() => handleReturn(order._id)}
                                                                            >
                                                                                Return Item
                                                                            </LuxuryButton>
                                                                        )}

                                                                        {order.trackingNumber && (
                                                                            <LuxuryButton
                                                                                variant="outline"
                                                                                size="small"
                                                                                onClick={() => handleTrackOrder(order)}
                                                                                icon={FaTruck}
                                                                            >
                                                                                Track Package
                                                                            </LuxuryButton>
                                                                        )}

                                                                        {order.isCancellable && (
                                                                            <LuxuryButton
                                                                                variant="outline"
                                                                                size="small"
                                                                                onClick={() => handleCancelOrder(order._id)}
                                                                                className="text-red-500 border-red-200 hover:bg-red-50"
                                                                            >
                                                                                Cancel Order
                                                                            </LuxuryButton>
                                                                        )}

                                                                        <button
                                                                            onClick={() => toggleOrderDetails(order._id)}
                                                                            className="ml-auto text-sm text-charcoal/50 hover:text-charcoal transition-colors"
                                                                        >
                                                                            Close details
                                                                        </button>
                                                                    </div>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </motion.div>
                                ) : (
                                    /* Empty State */
                                    <motion.div
                                        initial={{opacity: 0, scale: 0.95}}
                                        animate={{opacity: 1, scale: 1}}
                                        className="text-center py-16"
                                    >
                                        <motion.div
                                            animate={{
                                                y: [0, -10, 0]
                                            }}
                                            transition={{
                                                duration: 3,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                            className="inline-flex items-center justify-center mb-6"
                                        >
                                            <div className="relative">
                                                <div
                                                    className="w-24 h-24 rounded-2xl bg-gradient-to-br from-champagne/10 to-sage/10 flex items-center justify-center">
                                                    <FaBox className="w-12 h-12 text-charcoal/30"/>
                                                </div>
                                                <div
                                                    className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                                                    <FaReceipt className="w-5 h-5 text-sage"/>
                                                </div>
                                            </div>
                                        </motion.div>

                                        <div className="mb-8">
                                            <h2 className="text-3xl font-light text-charcoal mb-3">
                                                {orders.length === 0 ? "No orders yet" : "No orders found"}
                                            </h2>
                                            <p className="text-charcoal/60 max-w-md mx-auto leading-relaxed">
                                                {searchTerm || filter !== 'all' || timeRange !== 'all-time'
                                                    ? 'Try adjusting your filters or search term'
                                                    : 'Begin your KROMAIC journey with your first order. Discover products that transform your ritual.'}
                                            </p>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                            <Link to="/shop">
                                                <LuxuryButton
                                                    variant="primary"
                                                    size="large"
                                                    icon={FaShoppingBag}
                                                    iconPosition="left"
                                                    className="group"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        Shop Collection
                                                        <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                                                    </span>
                                                </LuxuryButton>
                                            </Link>

                                            {(searchTerm || filter !== 'all' || timeRange !== 'all-time') && (
                                                <LuxuryButton
                                                    variant="outline"
                                                    size="large"
                                                    onClick={() => {
                                                        setFilter('all');
                                                        setSearchTerm('');
                                                        setTimeRange('all-time');
                                                    }}
                                                >
                                                    Clear Filters
                                                </LuxuryButton>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Summary & CTA */}
                            {filteredOrders.length > 0 && (
                                <motion.div
                                    variants={fadeInUp}
                                    transition={{delay: 0.2}}
                                    className="mt-12 pt-12 border-t border-nude/30"
                                >
                                    <div className="grid md:grid-cols-2 gap-8 items-center">
                                        <div>
                                            <h3 className="font-heading text-2xl font-medium text-charcoal mb-4">
                                                Your Beauty Journey
                                            </h3>
                                            <p className="text-charcoal/60 mb-6">
                                                Over time, you've invested ${stats.totalSpent.toFixed(2)} in premium skincare
                                                that's transformed your ritual. Continue exploring products that elevate
                                                your daily practice.
                                            </p>

                                            <div className="flex flex-wrap gap-4">
                                                <Link to="/shop">
                                                    <LuxuryButton
                                                        variant="primary"
                                                        size="medium"
                                                        icon={FaArrowRight}
                                                        iconPosition="right"
                                                    >
                                                        Continue Shopping
                                                    </LuxuryButton>
                                                </Link>

                                                <LuxuryButton
                                                    variant="outline"
                                                    size="medium"
                                                    onClick={() => alert('Export feature coming soon!')}
                                                    icon={FaDownload}
                                                >
                                                    Export Orders
                                                </LuxuryButton>
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-br from-champagne/5 to-sage/5 rounded-2xl p-6">
                                            <h4 className="font-medium text-charcoal mb-4">Order Insights</h4>
                                            <div className="space-y-4">
                                                <div>
                                                    <div className="flex justify-between mb-1">
                                                        <span className="text-sm text-charcoal/70">Average Order Value</span>
                                                        <span className="text-charcoal font-medium">
                                                            ${stats.total > 0 ? (stats.totalSpent / stats.total).toFixed(2) : '0.00'}
                                                        </span>
                                                    </div>
                                                    <div className="h-1 bg-nude/30 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-champagne to-sage rounded-full"
                                                            style={{width: stats.total > 0 ? '75%' : '0%'}}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <div className="flex justify-between mb-1">
                                                        <span className="text-sm text-charcoal/70">Orders Confirmed</span>
                                                        <span className="text-charcoal font-medium">
                                                            {stats.total > 0 ? ((stats.delivered / stats.total) * 100).toFixed(0) : '0'}%
                                                        </span>
                                                    </div>
                                                    <div className="h-1 bg-nude/30 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-sage rounded-full"
                                                            style={{width: `${stats.total > 0 ? (stats.delivered / stats.total) * 100 : 0}%`}}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Need Help Section */}
                            <motion.div
                                variants={fadeInUp}
                                transition={{delay: 0.3}}
                                className="mt-12 p-8 bg-gradient-to-r from-champagne/5 to-sage/5 rounded-2xl"
                            >
                                <div className="grid md:grid-cols-3 gap-6">
                                    <div className="text-center">
                                        <div
                                            className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                                            <FaTruck className="w-5 h-5 text-champagne"/>
                                        </div>
                                        <h4 className="font-medium text-charcoal mb-2">Shipping Questions?</h4>
                                        <p className="text-sm text-charcoal/60">Track packages and view shipping policies</p>
                                        <button className="text-sm text-champagne hover:text-champagne/80 mt-2">
                                            Learn More
                                        </button>
                                    </div>

                                    <div className="text-center">
                                        <div
                                            className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                                            <FaRedo className="w-5 h-5 text-champagne"/>
                                        </div>
                                        <h4 className="font-medium text-charcoal mb-2">Easy Returns</h4>
                                        <p className="text-sm text-charcoal/60">30-day return policy on all products</p>
                                        <button className="text-sm text-champagne hover:text-champagne/80 mt-2">
                                            Start Return
                                        </button>
                                    </div>

                                    <div className="text-center">
                                        <div
                                            className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                                            <FaStar className="w-5 h-5 text-champagne"/>
                                        </div>
                                        <h4 className="font-medium text-charcoal mb-2">Need Help?</h4>
                                        <p className="text-sm text-charcoal/60">Our beauty advisors are here for you</p>
                                        <button className="text-sm text-champagne hover:text-champagne/80 mt-2">
                                            Contact Support
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </>
                    )}
                </div>
            </motion.div>
            {/* Footer */}
            <Footer/>
        </div>
    );
};

export default Orders;